import React, { useState, useEffect } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Ban } from 'lucide-react';
import api from '../services/api';

/**
 * Alertas do beneficiário vindos do ERP (Situação 13 do levantamento da AME).
 *
 * DESENHO (decidido a partir dos dados reais da base):
 * - O texto médio tem ~69 caracteres, ou seja, cabe numa linha. Por isso o
 *   estado recolhido JÁ MOSTRA o teor do alerta mais relevante — não é só um
 *   ícone. Ícone puro dependeria do atendente clicar, e o custo de não ler um
 *   "beneficiário cancelado" é alto.
 * - Clicar expande: texto completo, vigência, quem registrou e os demais.
 * - CRÍTICOS (bloqueio no ERP ou inelegibilidade) já nascem ABERTOS e em
 *   vermelho — nesses o atendimento não deveria sequer começar sem ler.
 * - Só alertas VIGENTES; a API já deduplica textos repetidos.
 */

// O ERP marca "bloqueia atendimento" em 88 registros — 72 deles são
// "BENEFICIÁRIO CANCELADO". Já os ~192 de INELEGÍVEL (funcionário cedido)
// NÃO estão marcados, embora impeçam o atendimento na prática. Até a operação
// revisar isso no ERP, o CRM trata os dois como críticos.
const PADROES_CRITICOS = /INELEG|CANCELAD/i;

const ehCritico = (a) => Boolean(a?.bloqueia) || PADROES_CRITICOS.test(a?.texto || '');

const LIMITE_TEXTO = 200;

export default function AlertasBeneficiario({ cpf, compacto = false }) {
  const [alertas, setAlertas] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    let ativo = true;
    if (!cpf) return undefined;
    api.get(`/beneficiarios/cpf/${cpf}/alertas`)
      .then((r) => { if (ativo) setAlertas(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (ativo) setAlertas([]); }); // alerta é um plus: falhando, não quebra a tela
    return () => { ativo = false; };
  }, [cpf]);

  const critico = alertas.some(ehCritico);

  // Críticos nascem abertos — sem depender de clique.
  useEffect(() => { if (critico) setAberto(true); }, [critico]);

  if (!alertas.length) return null;

  const cor = critico ? '#dc2626' : '#d97706';
  const fundo = critico ? '#fef2f2' : '#fffbeb';
  const borda = critico ? '#fecaca' : '#fde68a';
  const principal = alertas[0];
  const resumo = (principal.texto || '').replace(/\s+/g, ' ').trim();

  return (
    <div style={{
      background: fundo, border: `1px solid ${borda}`, borderLeft: `4px solid ${cor}`,
      borderRadius: '10px', padding: compacto ? '8px 12px' : '10px 14px',
      marginBottom: '14px',
    }}>
      {/* Linha recolhida: já entrega o teor, não só o ícone */}
      <div onClick={() => setAberto((v) => !v)}
           style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer' }}>
        {critico ? <Ban size={16} color={cor} style={{ flexShrink: 0 }} />
                 : <AlertTriangle size={16} color={cor} style={{ flexShrink: 0 }} />}
        <span style={{ fontWeight: 700, color: cor, fontSize: '0.78rem', flexShrink: 0 }}>
          {alertas.length === 1 ? 'ALERTA' : `${alertas.length} ALERTAS`}
        </span>
        {!aberto && (
          <span style={{
            flex: 1, minWidth: 0, fontSize: '0.8rem', color: '#444',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {resumo}
          </span>
        )}
        <span style={{ marginLeft: 'auto', flexShrink: 0, color: cor }}>
          {aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </div>

      {aberto && (
        <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
          {alertas.map((a, i) => {
            const texto = (a.texto || '').trim();
            const longo = texto.length > LIMITE_TEXTO;
            const mostrarTudo = expandidos[i] || !longo;
            const critA = ehCritico(a);
            return (
              <div key={i} style={{
                background: '#fff', border: `1px solid ${critA ? '#fecaca' : '#f0e6c8'}`,
                borderRadius: '8px', padding: '10px 12px',
              }}>
                <div style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                  {mostrarTudo ? texto : `${texto.slice(0, LIMITE_TEXTO)}…`}
                </div>
                {longo && (
                  <button onClick={() => setExpandidos((e) => ({ ...e, [i]: !e[i] }))}
                          style={{ background: 'none', border: 'none', padding: 0, marginTop: '4px',
                                   cursor: 'pointer', color: cor, fontSize: '0.74rem', fontWeight: 600 }}>
                    {expandidos[i] ? 'ver menos' : 'ver mais'}
                  </button>
                )}
                <div style={{ marginTop: '7px', display: 'flex', flexWrap: 'wrap', gap: '10px',
                              fontSize: '0.7rem', color: '#999' }}>
                  {a.dt_inicio && <span>desde {a.dt_inicio}</span>}
                  {a.autor && <span>· {a.autor}</span>}
                  <span style={{ color: '#bbb' }}>· origem: {a.origem || 'ERP'}</span>
                  {a.bloqueia && (
                    <span style={{ color: '#dc2626', fontWeight: 700 }}>· sinalizado como bloqueio no ERP</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
